import React, { useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import styles from "./ClientForm.module.css";

const TWILIO_API_URL =
  "https://fox00890i2.execute-api.ap-southeast-2.amazonaws.com/prod/twilio";
const API_KEY = "B6tlsUua0xaRI4VFeb9175K3ee9A5WLuahjbyZwN";

function ApplicantDetails({ formData, setFormData, nextStep, prevStep }) {
  const [submitted, setSubmitted] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);
  const [loading, setLoading] = useState(false);

  // ---------- field updates ----------
  const handleChange = (field, value, applicant = "applicant1") => {
    setFormData({
      ...formData,
      [applicant]: { ...(formData[applicant] || {}), [field]: value },
    });
  };

  const handleBuyingWithSomeone = (value) => {
    setFormData({
      ...formData,
      buyingWithSomeone: value,
      applicant2: value ? formData.applicant2 || {} : {},
    });
  };

  // ---------- OTP logic (Applicant 1 only) ----------
  const sendOTP = async () => {
    const phone = formData.applicant1?.phone?.replace(/\D/g, "");
    if (!phone || phone.length < 9) {
      toast.error("Enter a valid Australian mobile number");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${TWILIO_API_URL}/send-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": API_KEY,
        },
        body: JSON.stringify({ phone: `+61${phone}` }),
      });
      const data = await res.json();

      if (data.success) {
        setOtpSent(true);
        toast.success(`OTP sent to +61${phone}`);
      } else toast.error("❌ Failed to send OTP. Try again.");
    } catch {
      toast.error("❌ Error sending OTP.");
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async () => {
    const phone = formData.applicant1?.phone?.replace(/\D/g, "");
    if (!otpCode) return toast.error("Enter your 6-digit OTP");

    setLoading(true);
    try {
      const res = await fetch(`${TWILIO_API_URL}/verify-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": API_KEY,
        },
        body: JSON.stringify({ phone: `+61${phone}`, otp: otpCode }),
      });
      const data = await res.json();

      if (data.success) {
        setOtpVerified(true);
        toast.success("✅ Mobile verified!");
      } else toast.error("❌ Incorrect OTP. Please try again.");
    } catch {
      toast.error("❌ Verification failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  // ---------- validation ----------
  const validateApplicant = (applicant, includeMaritalStatus = true) => {
    const data = formData[applicant] || {};
    const validations = {
      name: !!data.name && data.name.trim().length > 1,
      email: !!data.email && /\S+@\S+\.\S+/.test(data.email),
      phone:
        applicant === "applicant1"
          ? !!data.phone && otpVerified
          : !!data.phone && data.phone.length >= 8,
      dependants: !!data.dependants,
    };
    if (includeMaritalStatus) validations.maritalStatus = !!data.maritalStatus;
    return validations;
  };

  const applicant1Valid = validateApplicant("applicant1", true);
  const isValidApplicant1 = Object.values(applicant1Valid).every(Boolean);

  const applicant2Valid = formData.buyingWithSomeone
    ? validateApplicant("applicant2", false)
    : { name: true, email: true, phone: true, dependants: true };
  const isValidApplicant2 = Object.values(applicant2Valid).every(Boolean);

  const isFormValid = isValidApplicant1 && isValidApplicant2;

  const handleNext = () => {
    setSubmitted(true);
    if (isFormValid) nextStep();
    else toast.error("⚠️ Please complete all required fields");
  };

  // ---------- UI ----------
  return (
    <div className={styles.container}>
      <h3>Applicant Details</h3>

      {/* Applicant 1 */}
      <div className={styles.formRow}>
        <input
          type="text"
          placeholder="Full Name"
          value={formData.applicant1?.name || ""}
          onChange={(e) => handleChange("name", e.target.value, "applicant1")}
        />

        <input
          type="email"
          placeholder="you@example.com"
          value={formData.applicant1?.email || ""}
          onChange={(e) => handleChange("email", e.target.value, "applicant1")}
        />

        {/* ✅ Mobile + OTP for Applicant 1 */}
        <div className={styles.otpSection}>
          <input
            type="tel"
            placeholder="04xxxxxxxx"
            value={formData.applicant1?.phone || ""}
            onChange={(e) => handleChange("phone", e.target.value, "applicant1")}
            maxLength="10"
            disabled={otpVerified}
          />
          {!otpSent && !otpVerified && (
            <button
              className={styles.otpBtn}
              onClick={sendOTP}
              disabled={loading}
            >
              Send OTP
            </button>
          )}
          {otpSent && !otpVerified && (
            <>
              <input
                type="text"
                placeholder="Enter OTP"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value)}
                maxLength="6"
              />
              <button
                className={styles.verifyBtn}
                onClick={verifyOTP}
                disabled={loading}
              >
                Verify
              </button>
            </>
          )}
          {otpVerified && (
            <p className={styles.verifiedText}>✅ Verified</p>
          )}
        </div>

        <select
          value={formData.applicant1?.maritalStatus || ""}
          onChange={(e) =>
            handleChange("maritalStatus", e.target.value, "applicant1")
          }
        >
          <option value="">Select Marital Status</option>
          <option value="Single">Single</option>
          <option value="Married">Married</option>
          <option value="Defacto">Defacto</option>
        </select>

        <select
          value={formData.applicant1?.dependants || ""}
          onChange={(e) =>
            handleChange("dependants", e.target.value, "applicant1")
          }
        >
          <option value="">Number of Dependants</option>
          <option value="0">0</option>
          <option value="1">1</option>
          <option value="2">2</option>
          <option value="3+">3+</option>
        </select>
      </div>

      {/* Buying With Someone */}
      <p>Are you buying with someone else?</p>
      <div className={styles.buttonGroup}>
        <button
          type="button"
          className={formData.buyingWithSomeone ? styles.activeBtn : ""}
          onClick={() => handleBuyingWithSomeone(true)}
        >
          Yes
        </button>
        <button
          type="button"
          className={
            formData.buyingWithSomeone === false ? styles.activeBtn : ""
          }
          onClick={() => handleBuyingWithSomeone(false)}
        >
          No
        </button>
      </div>

      {/* Applicant 2 (no OTP) */}
      {formData.buyingWithSomeone && (
        <div className={styles.formRow}>
          <h4>Applicant 2 Details</h4>

          <input
            type="text"
            placeholder="Full Name"
            value={formData.applicant2?.name || ""}
            onChange={(e) => handleChange("name", e.target.value, "applicant2")}
          />

          <input
            type="email"
            placeholder="you@example.com"
            value={formData.applicant2?.email || ""}
            onChange={(e) => handleChange("email", e.target.value, "applicant2")}
          />

          <input
            type="tel"
            placeholder="04xxxxxxxx"
            value={formData.applicant2?.phone || ""}
            onChange={(e) => handleChange("phone", e.target.value, "applicant2")}
            maxLength="10"
          />

          <select
            value={formData.applicant2?.dependants || ""}
            onChange={(e) =>
              handleChange("dependants", e.target.value, "applicant2")
            }
          >
            <option value="">Number of Dependants</option>
            <option value="0">0</option>
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3+">3+</option>
          </select>
        </div>
      )}

      {/* Navigation */}
      <div className={styles.navBtns}>
        <button type="button" onClick={prevStep}>
          ← Back
        </button>
        <button
          className={styles.nextBtn}
          onClick={handleNext}
          disabled={!isFormValid}
        >
          Next →
        </button>
      </div>

      <ToastContainer position="top-center" autoClose={3000} hideProgressBar />
    </div>
  );
}

export default ApplicantDetails;
